<template>
  <div>
    <NavBar />
    <div class="page">
      <div class="col left-sidebar">
        <p class="heading">Filters</p>
        <div class="section">
          <div class="filter-group">
            <p class="heading">College</p>
            <select v-model="collegeId" class="form-select" aria-label="Select College...">
              <option value="-1">Select College...</option>
              <option v-for="college in colleges" :key="college.id" :value="college.id">
                {{ college.title }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <p class="heading">Help Type</p>
            <select v-model="helpType" class="form-select" aria-label="Select Help Type...">
              <option value="">Select Help Type...</option>
              <option value="test_prep">Standardized Tests</option>
              <option value="college_applications">College Applications</option>
              <option value="financial_aid">Financial Aid</option>
              <option value="interview_prep">Interview Prep</option>
              <option value="general_info">General School Info</option>
            </select>
          </div>
          <div class="filter-group">
            <p class="heading">Specific Type of Help</p>
            <div class="help-type-dropdown-wrapper">
              <button 
                type="button" 
                class="help-type-dropdown-button"
                @click="toggleHelpTypeDropdown"
              >
                <span>{{ selectedHelpTypesText }}</span>
                <span class="dropdown-arrow">▼</span>
              </button>
              <div v-if="showHelpTypeDropdown" class="help-type-dropdown-menu">
                <label 
                  v-for="option in helpTypeOptions" 
                  :key="option.value"
                  class="help-type-option"
                  :class="{ selected: selectedHelpTypes.includes(option.value) }"
                >
                  <div class="checkbox-wrapper">
                    <div 
                      class="custom-checkbox" 
                      :class="{ checked: selectedHelpTypes.includes(option.value) }"
                    >
                      <span v-if="selectedHelpTypes.includes(option.value)" class="checkmark">✔</span>
                    </div>
                    <input 
                      type="checkbox" 
                      :value="option.value"
                      v-model="selectedHelpTypes"
                      @change="searchCounselors"
                    >
                  </div>
                  <span class="option-label">{{ option.label }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="filter-group">
            <p class="heading">Rating</p>
            <div>
              <div class="star-wrapper" 
                   v-for="index in 5" 
                   :key="index" 
                   @click="updateRating(index)">
                <span v-if="index <= minRating" class="star">&#9733;</span>
                <span v-else class="star">&#9734;</span>
              </div>
            </div>
          </div>
          <div class="filter-group">
            <p class="heading">Availability</p>
            <div id="online-now-container">
              <input v-model="isOnline" class="form-check-input" type="checkbox" id="flexCheckDefault">
              <label class="form-check-label" for="flexCheckDefault">Online Now</label>
            </div>
          </div>
          <div class="filter-group">
            <button type="button" class="clear-button" @click="clearFilters">Clear Filters</button>
          </div>
        </div>
      </div>
      <div class="col main">
        <div class="search">
          <p class="heading">Search</p>
          <div class="search-bar">
            <input 
              type="text" 
              v-model="names" 
              placeholder="Search by Counselor name or school name"
              @keyup.enter="searchCounselors"
              class="search-input"
            >
            <button type="button" class="search-button" @click="searchCounselors">Search</button>
          </div>
          
          <!-- Filter Chips -->
          <div v-if="hasActiveFilters" class="filter-chips">
            <div v-if="names" class="chip">
              <span>Search: {{ names }}</span>
              <button @click="removeFilter('names')" class="chip-remove">×</button>
            </div>
            <div v-if="collegeId !== -1" class="chip">
              <span>College: {{ getCollegeName(collegeId) }}</span>
              <button @click="removeFilter('college')" class="chip-remove">×</button>
            </div>
            <div v-for="helpType in selectedHelpTypes" :key="helpType" class="chip">
              <span>{{ getHelpTypeLabel(helpType) }}</span>
              <button @click="removeHelpType(helpType)" class="chip-remove">×</button>
            </div>
            <div v-if="helpType" class="chip">
              <span>Help: {{ helpType }}</span>
              <button @click="removeFilter('helpType')" class="chip-remove">×</button>
            </div>
            <div v-if="minRating > 0" class="chip">
              <span>Rating: {{ minRating }}+ stars</span>
              <button @click="removeFilter('rating')" class="chip-remove">×</button>
            </div>
            <div v-if="isOnline" class="chip">
              <span>Online Now</span>
              <button @click="removeFilter('online')" class="chip-remove">×</button>
            </div>
          </div>
          
          <div v-if="isLoading" class="loading">
            Loading counselors...
          </div>
          <div v-else-if="error" class="error">
            {{ error }}
          </div>
          <div v-else-if="!counselors || counselors.length === 0" class="no-results">
            No counselors found matching your criteria
          </div>
          <div v-else>
            <CounselorCard 
              v-for="counselor in counselors" 
              :key="counselor.user_id" 
              :counselor="counselor" 
            />
          </div>
        </div>
      </div>
      <div class="col right-sidebar">
        <p class="heading">Messages</p>
        <div class="section messages-section">
          <div v-if="contactsLoading" class="loading">Loading messages...</div>
          <div v-else-if="contactsError" class="error">{{ contactsError }}</div>
          <div v-else-if="!isLoggedIn" class="no-messages">
            <p>Log in to see your messages</p>
          </div>
          <div v-else-if="contacts.length === 0" class="no-messages">
            <p>No messages yet</p>
          </div>
          <!-- <div v-else class="sidebar-messages-list">
            <div 
              v-for="contact in contacts" 
              :key="contact.userId" 
              @click="selectRecipient(contact.userId, contact.name)" 
              class="sidebar-message-item"
            >
              <div v-if="!contact.profilePic" class="profile-initials">
                {{ getInitials(contact.name) }}
              </div>
              <img v-else :src="contact.profilePic" :alt="contact.name" class="profile-img" />
              <div class="message-content">
                <div class="message-header">
                  <span class="contact-name">{{ contact.name }}</span>
                  <span v-if="contact.unreadCount > 0" class="unread-badge">{{ contact.unreadCount }}</span>
                </div>
                <p class="last-message">{{ contact.lastMessage || 'No messages yet' }}</p>
              </div>
            </div>
          </div> -->
          <div v-else class="messages-list">
            <div
              v-for="contact in contacts"
              :key="contact.userId"
              @click="selectRecipient(contact.userId, contact.name)"
              class="message-item"
            >
              <!-- Avatar -->
              <div class="avatar">
                <img 
                  v-if="contact.profilePic" 
                  :src="contact.profilePic" 
                  :alt="contact.name"
                />
                <div v-else class="avatar-initials">{{ getInitials(contact.name) }}</div>
              </div>

              <!-- Message preview -->
              <div class="message-info">
                <div class="top-row">
                  <span class="name">{{ contact.name }}</span>
                  <span
                    v-if="contact.unreadCount > 0"
                    class="badge"
                  >
                    {{ contact.unreadCount }}
                  </span>
                </div>

                <div class="bottom-row">
                  <span class="preview">
                    {{ contact.lastMessage || 'No messages yet' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import axios from "axios";
import CounselorCard from "./../../components/CounselorCard.vue";
import NavBar from "./../../components/NavBar.vue";
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import io from 'socket.io-client';
import auth from '../../auth/auth.js';

const API_BASE = process?.env?.VUE_APP_API_BASE_URL ?? '/api';

export default {
  name: "BrowseCounselorsView",
  components: { CounselorCard, NavBar },
  setup() {
    const router = useRouter();
    const contacts = ref([]);
    const contactsLoading = ref(true);
    const contactsError = ref(null);
    const socket = ref(null);
    const isLoggedIn = ref(false);
    let senderId = null;

    const selectRecipient = (userId, name) => {
      router.push({
        name: 'messages',
        query: { recipient_id: userId, name: name }
      });
    };

    const getInitials = (name) => {
      if (!name) return '';
      const names = name.split(' ');
      return names.length >= 2 
        ? `${names[0][0] || ''}${names[1][0] || ''}`.toUpperCase()
        : (names[0][0] || '').toUpperCase();
    };

    const fetchMessageItems = async () => {
      if (!senderId) return;
      
      try {
        const response = await fetch(`${API_BASE}/messageItems/${senderId}`);
        if (!response.ok) throw new Error('Failed to fetch message items');
        const data = await response.json();
        
        contacts.value = data.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        contactsLoading.value = false;
      } catch (error) {
        contactsError.value = "Error loading messages: " + error.message;
        contactsLoading.value = false;
      }
    };

    const updateContactWithNewMessage = (newMessage) => {
      const otherUserId = newMessage.sender_id === senderId 
        ? newMessage.recipient_id 
        : newMessage.sender_id;

      const existingIdx = contacts.value.findIndex(c => c.userId === otherUserId);
      
      if (existingIdx !== -1) {
        contacts.value[existingIdx].lastMessage = newMessage.message;
        contacts.value[existingIdx].timestamp = newMessage.timestamp;
        if (newMessage.sender_id !== senderId) {
          contacts.value[existingIdx].unreadCount = (contacts.value[existingIdx].unreadCount || 0) + 1;
        }
        // Re-sort to move updated contact to top
        contacts.value = contacts.value.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
      } else {
        // New contact - refetch all items
        fetchMessageItems();
      }
    };

    onMounted(async () => {
      isLoggedIn.value = await auth.isAuthenticated();
      
      if (!isLoggedIn.value) {
        contactsLoading.value = false;
        return;
      }

      senderId = auth.getUserID();
      await fetchMessageItems();

      const SOCKET_URL = process?.env?.VUE_APP_SOCKET_URL ?? window.location.origin;
      socket.value = io(SOCKET_URL, {
        path: '/socket.io',
        transports: ['websocket', 'polling'],
        withCredentials: true,
      });

      socket.value.on('connect', () => {
        socket.value.emit('user_login', senderId);
      });

      socket.value.on('new_message', (newMessage) => {
        updateContactWithNewMessage(newMessage);
      });
    });

    onUnmounted(() => {
      if (socket.value) {
        socket.value.disconnect();
      }
    });

    return { 
      selectRecipient, 
      contacts, 
      contactsLoading, 
      contactsError, 
      isLoggedIn,
      getInitials 
    };
  },
  data() {
    const urlParams = new URLSearchParams(window.location.search);
    const helpTypesParam = urlParams.get("search_help_types");
    return {
      names: urlParams.get("names") || "",
      collegeId: urlParams.get("college_id") || -1,
      helpType: urlParams.get("help_type") || "",
      selectedHelpTypes: helpTypesParam ? helpTypesParam.split(',') : [],
      showHelpTypeDropdown: false,
      isOnline: urlParams.get("is_online") === "true" || false,
      minRating: Number(urlParams.get("min_rating")) || 0,
      colleges: [],
      counselors: [],
      isLoading: true,
      error: null,
      helpTypeOptions: [
        { value: 'psat', label: 'PSAT' },
        { value: 'sat', label: 'SAT' },
        { value: 'sat_subject', label: 'SAT Subject Tests' },
        { value: 'act', label: 'ACT' },
        { value: 'toefl', label: 'TOEFL' },
        { value: 'ap', label: "AP's" },
        { value: 'ged', label: 'GED' },
        { value: 'college_list', label: 'College list' },
        { value: 'common_app', label: 'Common application' },
        { value: 'essays', label: 'College essays' },
        { value: 'interview', label: 'Interview preparation' },
        { value: 'financial_aid', label: 'Financial aid' }
      ]
    };
  },
  computed: {
    selectedHelpTypesText() {
      if (this.selectedHelpTypes.length === 0) return 'Select Specific Type of Help...';
      if (this.selectedHelpTypes.length === 1) return this.getHelpTypeLabel(this.selectedHelpTypes[0]);
      return `${this.selectedHelpTypes.length} types selected`;
    },
    hasActiveFilters() {
      return this.names || this.collegeId !== -1 || this.selectedHelpTypes.length > 0 || 
             this.helpType || this.minRating > 0 || this.isOnline;
    }
  },
  methods: {
    toggleHelpTypeDropdown() { this.showHelpTypeDropdown = !this.showHelpTypeDropdown; },
    getHelpTypeLabel(value) {
      const opt = this.helpTypeOptions.find(o => o.value === value);
      return opt ? opt.label : value;
    },
    getCollegeName(collegeId) {
      const college = this.colleges.find(c => c.id === collegeId);
      return college ? college.title : 'Unknown';
    },
    removeFilter(filterType) {
      const map = { names: '', college: -1, helpType: '', rating: 0, online: false };
      const key = filterType === 'college' ? 'collegeId' : filterType === 'rating' ? 'minRating' : filterType === 'online' ? 'isOnline' : filterType;
      this[key] = map[filterType];
      this.searchCounselors();
    },
    removeHelpType(ht) {
      this.selectedHelpTypes = this.selectedHelpTypes.filter(h => h !== ht);
      this.searchCounselors();
    },
    updateRating(index) {
      this.minRating = index === this.minRating ? 0 : index;
      this.searchCounselors();
    },
    async searchCounselors() {
      try {
        this.isLoading = true;
        this.error = null;
        const params = new URLSearchParams({
          names: this.names || "",
          college_id: this.collegeId || -1,
          help_type: this.helpType || "",
          search_help_types: this.selectedHelpTypes.join(','),
          is_online: this.isOnline || false,
          min_rating: this.minRating || 0
        });
        const res = await fetch(`${API_BASE}/search-counselors?${params.toString()}`);
        if (!res.ok) throw new Error("Failed to fetch counselors");
        const data = await res.json();
        this.counselors = data.counselors || [];
        window.history.pushState({}, "", `${window.location.pathname}?${params.toString()}`);
      } catch (e) {
        this.error = "Error loading counselors: " + e.message;
        this.counselors = [];
      } finally {
        this.isLoading = false;
      }
    },
    clearFilters() {
      this.names = "";
      this.collegeId = -1;
      this.helpType = "";
      this.selectedHelpTypes = [];
      this.isOnline = false;
      this.minRating = 0;
      this.searchCounselors();
    }
  },
  watch: {
    helpType() { this.searchCounselors(); },
    collegeId() { this.searchCounselors(); },
    isOnline() { this.searchCounselors(); }
  },
  async mounted() {
    try {
      this.isLoading = true;
      document.addEventListener('click', (e) => {
        const wrapper = this.$el.querySelector('.help-type-dropdown-wrapper');
        if (wrapper && !wrapper.contains(e.target)) this.showHelpTypeDropdown = false;
      });
      let res = await fetch(`/api/colleges`);
      if (!res.ok) throw new Error("Failed to fetch colleges");
      let data = await res.json();
      if (data.colleges) this.colleges = data.colleges.sort((a, b) => a.title.localeCompare(b.title));
      await this.searchCounselors();
    } catch (e) {
      this.error = "Error initializing page: " + e.message;
      this.counselors = [];
      this.colleges = [];
    } finally {
      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
.loading, .error, .no-results, .no-messages {
  text-align: center;
  padding: 20px;
  margin: 20px 0;
  border-radius: 4px;
}
.error { color: #721c24; background-color: #f8d7da; border: 1px solid #f5c6cb; }
.loading { color: #3867ae; background-color: #f8f9fa; }
.no-results { color: #856404; background-color: #fff3cd; border: 1px solid #ffeeba; }
.no-messages { color: #6c757d; padding: 40px 20px; }

select { border-radius: 0.5em; border-color: gray; color: gray; padding: 5px; margin: 5px 0; }

.page {
  position: relative;
  background-color: #eff4ff;
  margin-top: 80px;
  padding: 3em 2em;
  display: flex;
  min-height: calc(100vh - 80px);
}
.heading { color: #3867ae; font-size: 1.2em; margin-bottom: 0.4em; text-align: left; }
.section { background-color: white; border-radius: 12px; padding: 1em; }
.filter-group { position: relative; margin-bottom: 2em; }
.filter-group .heading { text-transform: uppercase; font-weight: bold; font-size: 0.85em; letter-spacing: 0.08em; margin-bottom: 0.7em; }
.star-wrapper { position: relative; display: inline-block; cursor: pointer; }
.star { font-size: 2.5em; color: #ffd966; }
.search-bar { display: flex; gap: 10px; margin-bottom: 1em; align-items: center; }
.search-input { flex: 1; font-size: 1em; padding: 10px; border-radius: 4px; border: 1px solid #ced4da; min-width: 0; }
.help-type-dropdown-wrapper { position: relative; width: 100%; }
.help-type-dropdown-button { width: 100%; font-size: 1em; padding: 10px; border-radius: 4px; border: 1px solid #ced4da; background-color: white; color: gray; cursor: pointer; display: flex; justify-content: space-between; align-items: center; text-align: left; }
.help-type-dropdown-button:hover { border-color: #3867ae; }
.dropdown-arrow { margin-left: 8px; font-size: 0.8em; }
.help-type-dropdown-menu { position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px; background-color: white; border: 1px solid #ced4da; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); z-index: 1000; max-height: 300px; overflow-y: auto; }
.help-type-option { display: flex; align-items: center; padding: 10px 12px; cursor: pointer; transition: background-color 0.2s; }
.help-type-option:hover { background-color: #f0f0f0; }
.help-type-option.selected { background-color: #e3f2fd; }
.checkbox-wrapper { position: relative; display: flex; align-items: center; margin-right: 10px; }
.help-type-option input[type="checkbox"] { opacity: 0; position: absolute; cursor: pointer; }
.custom-checkbox { width: 20px; height: 20px; border: 2px solid #3867ae; border-radius: 4px; display: flex; align-items: center; justify-content: center; background-color: white; transition: all 0.2s; }
.custom-checkbox.checked { background-color: #3867ae; border-color: #3867ae; }
.checkmark { color: white; font-weight: bold; font-size: 14px; line-height: 1; }
.option-label { color: #333; flex: 1; }
.search-button { background-color: #3867ae; font-size: 1em; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; white-space: nowrap; flex-shrink: 0; }
.search-button:hover { background-color: #2d5392; }
.filter-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 1.5em; }
.chip { display: inline-flex; align-items: center; background-color: #e3f2fd; color: #1976d2; padding: 6px 12px; border-radius: 16px; font-size: 0.9em; gap: 8px; }
.chip-remove { background: none; border: none; color: #1976d2; font-size: 1.3em; cursor: pointer; padding: 0; line-height: 1; font-weight: bold; margin-left: 4px; }
.chip-remove:hover { color: #0d47a1; }
.clear-button { background-color: #6c757d; color: white; font-size: 1em; border: none; padding: 8px; border-radius: 4px; cursor: pointer; width: 100%; }
.clear-button:hover { background-color: #5a6268; }
.left-sidebar, .right-sidebar { width: 20%; }
.main { flex-grow: 2; }
.col { margin: 16px; }
.form-check-input[type="checkbox"] { margin: 0.5em; }
#online-now-container { display: flex; align-items: center; gap: 8px; }

/* Messages Sidebar Styles */
.messages-section { max-height: 500px; overflow-y: auto; }
.messages-list { display: flex; flex-direction: column; gap: 4px; }
.message-item {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
  border: 1px solid #eee;
  background-color: #fff;
}
.message-item:hover { background-color: #f0f4ff; }
.profile-img { width: 40px; height: 40px; border-radius: 50%; margin-right: 12px; object-fit: cover; flex-shrink: 0; }
.profile-initials {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 12px;
  background-color: #3867ae;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}
.message-content { flex: 1; min-width: 0; }
.message-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.contact-name { font-weight: 600; font-size: 14px; color: #1a1a1a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.unread-badge {
  background-color: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  flex-shrink: 0;
  margin-left: 8px;
}
.last-message {
  font-size: 13px;
  color: #666;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- Right Sidebar Messages Redesign --- */
.messages-section {
  padding: 0;
  background: white;
  border-radius: 12px;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.message-item {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  background: #f9f9fb;
  transition: background 0.15s ease, transform 0.1s ease;
}

.message-item:hover {
  background: #eef3ff;
}

.message-item:active {
  transform: scale(0.98);
}

/* Avatar */
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  background: #d8e1ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  color: #3867ae;
  font-weight: bold;
  font-size: 1.1rem;
}

/* Message Details */
.message-info {
  flex: 1;
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
}

.preview {
  font-size: 0.85rem;
  color: #636363;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Unread Badge */
.badge {
  background: #3867ae;
  color: white;
  font-size: 0.75rem;
  padding: 3px 7px;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
}

</style>